import { useState, ReactElement } from 'react';
import { isEmpty } from 'lodash';
import { useApi } from 'lib/contexts/ApplicationState';
import { useSnackbar } from 'notistack';
import { IMPORT_STEPS, SOURCES, SOURCE_IDS } from './constants';
import { SelectSource } from './Steps/SelectSource';
import { SelectUsername } from './Steps/SelectUsername';
import { ImportPosts } from './Steps/ImportPosts';
import { ImportUrls } from './Steps/ImportUrls';
import { ShowFinish } from './Steps/ShowFinish';
import { SingleLink } from './Steps/SingleLink';
import { LinkedInPage } from './Steps/LinkedInPage';

const pathForSource = (sourceId: number | null, username: string) => {
  switch (sourceId) {
  case SOURCE_IDS.instagram:
    return `/instagramPosts/${username}`;
  case SOURCE_IDS.twitter:
    return `/twitterProxy/${username}/tweets`;
  case SOURCE_IDS.tiktok:
    return `/tiktokProxy/${username}/tiktoks`;
  case SOURCE_IDS.youtube:
    return `/youtubeProxy/${username}/videos`;
  case SOURCE_IDS.twitch:
    return `/twitchProxy/${username}/videos`;
  case SOURCE_IDS.linkedin:
    return `/linkedin/posts/${username}`;
  case SOURCE_IDS.threads:
    return `/threads/${username}`;
  default:
    return '';
  }
};

interface Props {
  onClose: () => void;
}

export const ImportWizard = ({ onClose }: Props) => {
  const API = useApi();

  const [step, setStep] = useState(IMPORT_STEPS.selectSource);
  const [source, setSource] = useState<number|null>(null);
  const [title, setTitle] = useState('');
  const [username, setUsername] = useState('');

  const [posts, setPosts] = useState<any[]>([]);
  const [pageAfter, setPageAfter] = useState<string|undefined>(undefined);

  const [importId, setImportId] = useState('');
  const [imported, setImported] = useState<string[]>([]);
  const [errMsg, setErrMsg] = useState<string | ReactElement>('');

  const [isLoading, setIsLoading] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const selectSource = (src: number) => {
    setSource(src);
    setTitle(SOURCES[src]?.title);
    setPageAfter(undefined);
    if (src === SOURCE_IDS.listOfUrls) {
      setStep(IMPORT_STEPS.importUrls);
    } else if (src === SOURCE_IDS.singleLink) {
      setStep(IMPORT_STEPS.singleLink);
    } else if (src === SOURCE_IDS.linkedin) {
      setStep(IMPORT_STEPS.importLinkedin);
    } else {
      setStep(IMPORT_STEPS.selectUsername);
    }
  };

  const getPosts = async () => {
    const path = pathForSource(source, username);
    try {
      const { data, paging } = await API.getCursorPagination(
        path, {}, { returnFullJson: true }, undefined, pageAfter,
      ) as any;
      setPageAfter(paging?.cursors?.after);
      return data;
    } catch (e) {
      if (source === SOURCE_IDS.instagram) {
        setErrMsg('Username either invalid or not a business account. We can only bulk import Instagram business accounts.');
      } else if (source === SOURCE_IDS.threads) {
        setErrMsg(
          <>
            Username either invalid or not authed. We can only bulk import Threads authed accounts.
            Go to
            {' '}
            <a href="/settings/accounts" className="underline">your settings</a>
            {' '}
            to connect.
          </>,
        );
      } else {
        setErrMsg('Unable to find posts for that user. Please try again.');
      }
    }
    return null;
  };

  const loadMorePosts = async () => {
    setIsLoading(true);
    try {
      const morePosts = await getPosts();
      if (!morePosts || isEmpty(morePosts)) {
        enqueueSnackbar('There are no more posts available at this time.', { variant: 'warning' });
        setIsLoading(false);
        setPageAfter(undefined);
        return;
      }
      setPosts([...posts, ...morePosts]);
      setIsLoading(false);
    } catch (err: any) {
      setIsLoading(false);
      enqueueSnackbar(`Something went wrong: ${err.message}`, { variant: 'error' });
    }
  };

  const selectUsername = async () => {
    setErrMsg('');
    setIsLoading(true);
    const data = await getPosts();

    setIsLoading(false);
    if (!data || isEmpty(data)) {
      return;
    }
    // remove any null values from the post array TODO: handle on backend
    setPosts(data.filter(Boolean) as any);
    setStep(IMPORT_STEPS.importPosts);
  };

  const showImported = async (result: any, newImportId: string) => {
    setImported(result);
    setImportId(newImportId);
    setStep(IMPORT_STEPS.showFinish);
  };

  switch (step) {
  case IMPORT_STEPS.selectUsername:
    return (
      <SelectUsername
        username={username}
        networkName={title}
        usernameError={errMsg}
        isLoading={isLoading}
        onBack={() => setStep(IMPORT_STEPS.selectSource)}
        onClose={onClose}
        onChange={setUsername}
        onNext={selectUsername}
      />
    );

  case IMPORT_STEPS.importUrls:
    return (
      <ImportUrls
        onClose={onClose}
        onNext={onClose}
        onBack={() => setStep(IMPORT_STEPS.selectSource)}
      />
    );

  case IMPORT_STEPS.singleLink:
    return (
      <SingleLink
        onClose={onClose}
        onBack={() => setStep(IMPORT_STEPS.selectSource)}
      />
    );

  case IMPORT_STEPS.importLinkedin:
    return (
      <LinkedInPage
        onClose={onClose}
        onBack={() => setStep(IMPORT_STEPS.selectSource)}
        onNext={selectUsername}
        onChange={setUsername}
        pagename={username}
      />
    );

  case IMPORT_STEPS.importPosts:
    return (
      <ImportPosts
        networkName={title}
        username={username}
        posts={posts}
        onLoadMore={loadMorePosts}
        onClose={onClose}
        onNext={showImported}
        onBack={() => setStep(IMPORT_STEPS.selectUsername)}
        hasMorePages={!!pageAfter}
        postsAreLoading={isLoading}
      />
    );

  case IMPORT_STEPS.showFinish:
    return (
      <ShowFinish
        imported={imported}
        importId={importId}
        username={username}
        onClose={onClose}
      />
    );
  default:
    return (
      <SelectSource onClose={onClose} onSelect={selectSource} />
    );
  }
};
